import type { ISprint } from '@pulse/shared';

import { apiFetch } from '../../../lib/api-client';
import { extractErrorMessage } from '../../../lib/api-error.util';

const base = (projectId: string) => `/api/v1/projects/${projectId}/sprints`;

export interface CreateSprintInput {
  name: string;
  // `null` clears Sprint Goal on update (the backend column is nullable); `undefined`/omitted
  // leaves it unchanged. name/startDate/endDate have no clear semantics — all three are required.
  sprintGoal?: string | null;
  startDate: string;
  endDate: string;
}
export type UpdateSprintInput = Partial<CreateSprintInput>;

export async function fetchSprints(projectId: string): Promise<ISprint[]> {
  const res = await apiFetch(base(projectId));
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch sprints'));
  return res.json() as Promise<ISprint[]>;
}

export async function createSprint(projectId: string, data: CreateSprintInput): Promise<ISprint> {
  const res = await apiFetch(base(projectId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to create sprint'));
  return res.json() as Promise<ISprint>;
}

export async function updateSprint(projectId: string, sprintId: string, data: UpdateSprintInput): Promise<ISprint> {
  const res = await apiFetch(`${base(projectId)}/${sprintId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update sprint'));
  return res.json() as Promise<ISprint>;
}

export async function setSprintActive(projectId: string, sprintId: string, active: boolean): Promise<ISprint> {
  const res = await apiFetch(`${base(projectId)}/${sprintId}/${active ? 'activate' : 'deactivate'}`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update sprint status'));
  return res.json() as Promise<ISprint>;
}

export async function setSprintClosed(projectId: string, sprintId: string, closed: boolean): Promise<ISprint> {
  const res = await apiFetch(`${base(projectId)}/${sprintId}/${closed ? 'close' : 'reopen'}`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update sprint status'));
  return res.json() as Promise<ISprint>;
}
